import { makeStyles } from "$components/mui-styles";
import {
  IconButton,
  TextField as MuiTextField,
  type OutlinedTextFieldProps as MuiTextFieldProps,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import React from "react";
import HelperText, { type ErrorMessage } from "./HelperText";

export type TextFieldProps = Omit<MuiTextFieldProps, "error" | "variant"> & {
  "data-testid"?: string;
  helperText?: string;
  /**
   * Force the helper row not to be displayed.
   * This will suppress the any errors from being shown but will set the height of the component to be only
   * the height of the input; rather than including whitespace for the error/helper message to render in.
   */
  hideHelper?: boolean;
  error?: string | ErrorMessage;
  variant?: "outlined" | "filled" | "standard";
  onClear?: () => void;
  clearButtonTitle?: string;
};

const useStyles = makeStyles({
  clearButton: {
    // Negative margin to offset padding around IconButton within input
    margin: -12,
  },
  clearIcon: {
    fontSize: 24,
  },
  clearButtonFallback: {
    width: 48,
    height: 48,
    display: "inline-block",
    margin: -12,
  },
  // The theme's 16px-top / 8px-bottom input padding leaves room for the
  // inside-floating label; without a label the text sits low in the box.
  // Same 24px total, so no height change — just recentred.
  unlabelledInput: {
    paddingTop: 12,
    paddingBottom: 12,
  },
});

type EndAdornmentProps = Pick<
  TextFieldProps,
  "onClear" | "value" | "clearButtonTitle"
> & {
  fallback?: React.ReactNode;
};

const EndAdornment = ({
  onClear: handleClear,
  value,
  fallback,
  clearButtonTitle = "Clear",
}: EndAdornmentProps) => {
  const classes = useStyles();

  if (value) {
    return (
      <IconButton
        onClick={handleClear}
        className={classes.clearButton}
        title={clearButtonTitle}
      >
        <Close color="secondary" className={classes.clearIcon} />
      </IconButton>
    );
  }

  return fallback ? (
    <>{fallback}</>
  ) : (
    <span className={classes.clearButtonFallback} />
  );
};

const TextFieldComp = (props: TextFieldProps, ref: React.Ref<HTMLElement>) => {
  const {
    "data-testid": dataTestId = undefined,
    error = undefined,
    helperText = undefined,
    hideHelper = undefined,
    id: inputId,
    name,
    inputProps = {},
    InputProps = {},
    variant = "outlined",
    value,
    onClear: handleClear = undefined,
    clearButtonTitle,
    multiline = false,
    color = "primary",
    disabled = false,
    fullWidth = false,
    type = "input",
    select = false,
    label = undefined,
    placeholder = undefined,
    required = undefined,
    children = undefined,
    rows = undefined,
    ...restProps
  } = props;

  const id = inputId ?? name;
  const classes = useStyles();

  const overrideProps = {
    id,
    inputRef: ref,
    value,
    inputProps: {
      "data-testid": dataTestId,
      ...inputProps,
      className: label
        ? inputProps.className
        : [inputProps.className, classes.unlabelledInput]
            .filter(Boolean)
            .join(" "),
    },
    InputProps: {
      ...InputProps,
      endAdornment:
        typeof handleClear === "function" ? (
          <EndAdornment
            onClear={handleClear}
            clearButtonTitle={clearButtonTitle}
            value={value}
            fallback={InputProps?.endAdornment}
          />
        ) : (
          InputProps?.endAdornment
        ),
    },
    InputLabelProps: {
      ...restProps.InputLabelProps,
      htmlFor: id,
      "aria-labelledby": restProps.InputLabelProps?.["aria-label"]
        ? undefined
        : name,
    },
    name,
    error: !!error,
  };

  return (
    <MuiTextField
      {...restProps}
      {...overrideProps}
      // `as any` required - see https://github.com/mui-org/material-ui/issues/15697#issuecomment-493419773
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      variant={variant as any}
      label={label}
      placeholder={placeholder}
      required={required}
      children={children}
      rows={rows}
      multiline={multiline}
      color={color}
      disabled={disabled}
      fullWidth={fullWidth}
      type={type}
      select={select}
      helperText={
        hideHelper ? null : <HelperText error={error}>{helperText}</HelperText>
      }
    />
  );
};

const TextField = React.forwardRef(TextFieldComp);

export default TextField;
